import React, { useCallback, useEffect } from 'react';
import { TextField, Checkbox, FormControlLabel, MenuItem, Select, InputLabel, FormControl, OutlinedInput, Box, Chip, Typography, Divider } from '@mui/material';

const paymentOptions = ['Visa', 'Mastercard', 'JCB', 'Cash', 'ABA', 'ACLEDA','PayPal', 'Stripe', 'Bank Transfer'];

import { Button } from '@mui/material';

const HotelPolicyForm = ({ form, setForm, onSubmit }) => {
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onSubmit?.(); // Call onSubmit from parent if provided
  }, [onSubmit]);

  return (
    <Box p={3}>
      <form onSubmit={handleSubmit}>
      {/* Basic Info */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Basic Info</Typography>
        <Divider sx={{ mb: 2 }} />
        <TextField
          fullWidth
          label="Check-in Time"
          margin="normal"
          value={form.checkIn || ''}
          onChange={(e) => setForm(prev => ({ ...prev, checkIn: e.target.value }))}
        />
        <TextField
          fullWidth
          label="Check-out Time"
          margin="normal"
          value={form.checkOut || ''}
          onChange={(e) => setForm(prev => ({ ...prev, checkOut: e.target.value }))}
        />
      </Box>

      {/* Children & Beds */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Children &amp; Beds</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.childrenAndBeds?.childrenAllowed || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  childrenAndBeds: {
                    ...prev.childrenAndBeds,
                    childrenAllowed: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Children Allowed"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.childrenAndBeds?.cribAvailable || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  childrenAndBeds: {
                    ...prev.childrenAndBeds,
                    cribAvailable: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Crib Available"
        />
        {form.childrenAndBeds?.cribAvailable && (
          <TextField
            fullWidth
            label="Crib Price"
            type="number"
            margin="normal"
            value={form.childrenAndBeds?.cribPrice || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                childrenAndBeds: {
                  ...prev.childrenAndBeds,
                  cribPrice: parseFloat(e.target.value),
                },
              }))
            }
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={form.childrenAndBeds?.extraBedAvailable || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  childrenAndBeds: {
                    ...prev.childrenAndBeds,
                    extraBedAvailable: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Extra Bed Available"
        />
        {form.childrenAndBeds?.extraBedAvailable && (
          <TextField
            fullWidth
            label="Extra Bed Price"
            type="number"
            margin="normal"
            value={form.childrenAndBeds?.extraBedPrice || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                childrenAndBeds: {
                  ...prev.childrenAndBeds,
                  extraBedPrice: parseFloat(e.target.value),
                },
              }))
            }
          />
        )}
        {form.childrenAndBeds?.childrenAllowed && (
          <TextField
            fullWidth
            label="Max Children Per Room"
            type="number"
            margin="normal"
            value={form.childrenAndBeds?.maxChildrenPerRoom || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                childrenAndBeds: {
                  ...prev.childrenAndBeds,
                  maxChildrenPerRoom: parseInt(e.target.value),
                },
              }))
            }
          />
        )}
      </Box>

      {/* Smoking Policy */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Smoking Policy</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.smokingPolicy?.allowed || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  smokingPolicy: {
                    ...prev.smokingPolicy,
                    allowed: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Smoking Allowed"
        />
        {form.smokingPolicy?.allowed && (
          <TextField
            fullWidth
            label="Smoking Policy Notes"
            margin="normal"
            value={form.smokingPolicy?.notes || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                smokingPolicy: {
                  ...prev.smokingPolicy,
                  notes: e.target.value,
                },
              }))
            }
          />
        )}
      </Box>

      {/* Age Restriction */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Age Restriction</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.ageRestriction?.required || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  ageRestriction: {
                    ...prev.ageRestriction,
                    required: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Age Restriction Required"
        />
        {form.ageRestriction?.required && (
          <TextField
            fullWidth
            label="Minimum Age"
            type="number"
            margin="normal"
            value={form.ageRestriction?.minimumAge || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                ageRestriction: {
                  ...prev.ageRestriction,
                  minimumAge: parseInt(e.target.value),
                },
              }))
            }
          />
        )}
      </Box>

      {/* Pets Policy */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Pets Policy</Typography>
        <Divider sx={{ mb: 2 }} />
        <TextField
          fullWidth
          label="Pets Policy"
          margin="normal"
          value={form.petsPolicy || ''}
          onChange={(e) => setForm(prev => ({ ...prev, petsPolicy: e.target.value }))}
        />
      </Box>

      {/* Quiet Hours */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Quiet Hours</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.quietHours?.enabled || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  quietHours: {
                    ...prev.quietHours,
                    enabled: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Quiet Hours Enabled"
        />
        {form.quietHours?.enabled && (
          <>
            <TextField
              fullWidth
              label="Quiet Hours Start"
              type="time"
              margin="normal"
              value={form.quietHours?.start || ''}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  quietHours: {
                    ...prev.quietHours,
                    start: e.target.value,
                  },
                }))
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              fullWidth
              label="Quiet Hours End"
              type="time"
              margin="normal"
              value={form.quietHours?.end || ''}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  quietHours: {
                    ...prev.quietHours,
                    end: e.target.value,
                  },
                }))
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </>
        )}
      </Box>

      {/* Internet */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Internet</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.internet?.available || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  internet: {
                    ...prev.internet,
                    available: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Internet Available"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.internet?.isFree || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  internet: {
                    ...prev.internet,
                    isFree: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Internet is Free"
        />
        {form.internet?.available && (
          <TextField
            fullWidth
            label="Internet Coverage"
            margin="normal"
            value={form.internet?.coverage || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                internet: {
                  ...prev.internet,
                  coverage: e.target.value,
                },
              }))
            }
          />
        )}
      </Box>

      {/* Parking */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Parking</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.parking?.available || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  parking: {
                    ...prev.parking,
                    available: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Parking Available"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.parking?.isFree || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  parking: {
                    ...prev.parking,
                    isFree: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Parking is Free"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.parking?.requiresReservation || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  parking: {
                    ...prev.parking,
                    requiresReservation: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Parking Requires Reservation"
        />
        {form.parking?.available && (
          <>
            <TextField
              fullWidth
              label="Parking Type"
              margin="normal"
              value={form.parking?.type || ''}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  parking: {
                    ...prev.parking,
                    type: e.target.value,
                  },
                }))
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.parking?.isFree || false}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      parking: {
                        ...prev.parking,
                        isFree: e.target.checked,
                      },
                    }))
                  }
                />
              }
              label="Parking is Free"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.parking?.requiresReservation || false}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      parking: {
                        ...prev.parking,
                        requiresReservation: e.target.checked,
                      },
                    }))
                  }
                />
              }
              label="Parking Requires Reservation"
            />
          </>
        )}
      </Box>

      {/* Damage Deposit */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Damage Deposit</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.damageDeposit?.required || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  damageDeposit: {
                    ...prev.damageDeposit,
                    required: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Damage Deposit Required"
        />
        {form.damageDeposit?.required && (
          <>
            <TextField
              fullWidth
              label="Damage Deposit Amount"
              type="number"
              margin="normal"
              value={form.damageDeposit?.amount || ''}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  damageDeposit: {
                    ...prev.damageDeposit,
                    amount: parseFloat(e.target.value),
                  },
                }))
              }
            />
            <TextField
              fullWidth
              label="Damage Deposit Refund Method"
              margin="normal"
              value={form.damageDeposit?.refundMethod || ''}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  damageDeposit: {
                    ...prev.damageDeposit,
                    refundMethod: e.target.value,
                  },
                }))
              }
            />
          </>
        )}
      </Box>

      {/* Party Policy */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Party Policy</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.partyPolicy?.allowed || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  partyPolicy: {
                    ...prev.partyPolicy,
                    allowed: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Party Allowed"
        />
        {form.partyPolicy?.allowed && (
          <TextField
            fullWidth
            label="Party Policy Notes"
            margin="normal"
            value={form.partyPolicy?.notes || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                partyPolicy: {
                  ...prev.partyPolicy,
                  notes: e.target.value,
                },
              }))
            }
          />
        )}
      </Box>

      {/* Cancellation Policy */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Cancellation Policy</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.cancellationPolicy?.cancellationAllowed || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  cancellationPolicy: {
                    ...prev.cancellationPolicy,
                    cancellationAllowed: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Allow Cancellation"
        />
        {form.cancellationPolicy?.cancellationAllowed && (
          <TextField
            fullWidth
            label="Cancellation Notice Days"
            type="number"
            margin="normal"
            value={form.cancellationPolicy?.cancellationNoticeDays || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                cancellationPolicy: {
                  ...prev.cancellationPolicy,
                  cancellationNoticeDays: parseInt(e.target.value),
                },
              }))
            }
          />
        )}
      </Box>

      {/* VAT */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>VAT</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.vat?.enabled || false}
              onChange={(e) =>
                setForm(prev => ({
                  ...prev,
                  vat: {
                    ...prev.vat,
                    enabled: e.target.checked,
                  },
                }))
              }
            />
          }
          label="Apply VAT"
        />
        {form.vat?.enabled && (
          <TextField
            fullWidth
            label="VAT Percentage"
            type="number"
            margin="normal"
            value={form.vat?.percentage || ''}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                vat: {
                  ...prev.vat,
                  percentage: parseFloat(e.target.value),
                },
              }))
            }
          />
        )}
      </Box>

      {/* Payment Methods */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Payment Methods</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl fullWidth margin="normal">
          <InputLabel id="payment-methods-label">Payment Methods</InputLabel>
          <Select
            labelId="payment-methods-label"
            multiple
            value={form.paymentMethods || []}
            onChange={(e) =>
              setForm(prev => ({
                ...prev,
                paymentMethods: e.target.value,
              }))
            }
            input={<OutlinedInput label="Payment Methods" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          >
            {paymentOptions.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={(form.paymentMethods || []).indexOf(option) > -1} />
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      </form>
    </Box>
  );
};

export default HotelPolicyForm;